import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Group, Textarea } from '@mantine/core';
import type { KeyboardEvent } from 'react';
import { useCallback, useContext, useMemo, useRef } from 'react';
import type { SlashCommand } from '../../../../api/agents-client/agent-client.type';
import { icons } from '../../../../assets/icons/icons';
import { UserContext } from '../../../../contexts/user';
import { Tooltip as UiTooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { FloraApprovalModeToggle } from '../../../flora/components/flora-approval-mode-toggle/flora-approval-mode-toggle';
import { formatMentionForMessage } from '../../../flora/components/flora-mention/flora-mention.helpers';
import type { MentionItem, SelectedMention } from '../../../flora/components/flora-mention/flora-mention.type';
import { MentionPill } from '../../../flora/components/flora-mention/mention-pill';
import { MentionPopover } from '../../../flora/components/flora-mention/mention-popover';
import { useMention } from '../../../flora/components/flora-mention/use-mention';
import { AgentAttachmentChip } from '../../../flora/components/chat/agent-attachment-chip';
import { SlashCommandPopover } from '../../../flora/components/chat/slash-commands/slash-command-popover';
import { useSlashCommandInput } from '../../../flora/components/chat/slash-commands/use-slash-command-input';
import { AgentModelSelector } from '../../../flora/components/chat/agent-model-selector';
import { FLORA_INPUT_PLACEHOLDER } from '../../../flora/components/chat/chat.constants';
import type { AgentAttachmentsBinding } from '../../../flora/components/chat/use-agent-attachments';
import { ACCEPTED_ATTACHMENT_EXTENSIONS } from '../../../flora/components/chat/use-agent-attachments.helpers';
import { useComposerFileDrop } from '../../../flora/components/chat/hooks/use-composer-file-drop';
import {
  FloraGlowIcon,
  FloraGradientWash,
  FloraLogoGlow,
} from '../../../flora/components/flora-landing/flora-landing.styles';
import { FloraPromptChips } from '../../../flora/components/flora-landing/flora-prompt-chips';
import { getGreeting } from './agent-landing.helpers';

interface AgentLandingProps {
  question: string;
  onQuestionChange: (value: string) => void;
  onSubmit: (formattedMessage?: string, displayMessage?: string) => void;
  isActiveQuestion: boolean;
  attachments: AgentAttachmentsBinding;
  /** The chat's thread id; empty until the first send or an attachment upload mints one. */
  threadId: string;
  dataTheme?: string;
}

export function AgentLanding({
  question,
  onQuestionChange,
  onSubmit,
  isActiveQuestion,
  attachments,
  threadId,
  dataTheme,
}: AgentLandingProps) {
  const { user } = useContext(UserContext);
  const firstName = user?.name?.split(' ')[0];
  const greeting = useMemo(() => getGreeting(firstName), [firstName]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  const {
    isOpen: isMentionOpen,
    searchQuery: mentionSearchQuery,
    allItems,
    boards,
    selectedMentions,
    handleInputChange,
    handleSelectMention,
    handleRemoveMention,
    closeMentionPopover,
    clearMentions,
  } = useMention(question);

  const {
    isOpen: isSlashOpen,
    searchQuery: slashSearchQuery,
    filteredGroups,
    flatFilteredCommands,
    selectedCommand,
    handleInputChange: handleSlashInputChange,
    handleSelectCommand,
    closePopover: closeSlashPopover,
    clearCommand,
    formatMessageWithCommand,
  } = useSlashCommandInput(question);

  const canSubmit = question.trim().length > 0 && !isActiveQuestion && !attachments.hasUploading;

  const { isDraggingOver, dropZoneRef, dropHandlers, fileInputRef, fileInputProps, triggerFilePicker } =
    useComposerFileDrop({ onFiles: attachments.addFiles, accept: ACCEPTED_ATTACHMENT_EXTENSIONS });

  const formatMessageWithMentions = useCallback((text: string, mentions: SelectedMention[]): string => {
    if (mentions.length === 0) return text;
    let formattedText = text;
    const sortedMentions = [...mentions].sort((a, b) => {
      const aPos = formattedText.indexOf(`[@${a.name}]`);
      const bPos = formattedText.indexOf(`[@${b.name}]`);
      return bPos - aPos;
    });
    for (const mention of sortedMentions) {
      const placeholder = `[@${mention.name}]`;
      formattedText = formattedText.replace(placeholder, formatMentionForMessage(mention));
    }
    return formattedText;
  }, []);

  const handleSubmit = useCallback(() => {
    if (!question.trim()) return;
    const withMentions = formatMessageWithMentions(question, selectedMentions);

    if (selectedCommand) {
      const backendMessage = formatMessageWithCommand(withMentions, selectedCommand);
      const displayMessage = withMentions;
      clearCommand();
      clearMentions();
      onSubmit(backendMessage, displayMessage);
    } else {
      clearMentions();
      onSubmit(withMentions);
    }
  }, [
    question,
    selectedMentions,
    selectedCommand,
    formatMessageWithMentions,
    formatMessageWithCommand,
    clearCommand,
    clearMentions,
    onSubmit,
  ]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Slash popover keyboard delegation (checked first)
    if (isSlashOpen && (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Tab')) {
      return;
    }
    if (e.key === 'Escape' && isSlashOpen) {
      e.preventDefault();
      closeSlashPopover();
      return;
    }

    // Mention popover keyboard delegation
    if (isMentionOpen && (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Tab')) {
      return;
    }
    if (e.key === 'Escape' && isMentionOpen) {
      e.preventDefault();
      closeMentionPopover();
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) handleSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.currentTarget.value;
    const cursorPosition = e.currentTarget.selectionStart || 0;
    onQuestionChange(value);
    handleSlashInputChange(value, cursorPosition);
    handleInputChange(value, cursorPosition);
  };

  const handleMentionSelect = (item: MentionItem) => {
    const { newValue, newCursorPosition } = handleSelectMention(item);
    onQuestionChange(newValue);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  const handlePillRemove = (mentionId: string, mentionName: string) => {
    const placeholder = `[@${mentionName}]`;
    const newValue = question.replace(placeholder, '').replace(/ {2,}/g, ' ').trim();
    onQuestionChange(newValue);
    handleRemoveMention(mentionId);
  };

  const triggerMention = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart || question.length;
    const before = question.substring(0, cursorPos);
    const after = question.substring(cursorPos);
    const needsSpace = before.length > 0 && before[before.length - 1] !== ' ' && before[before.length - 1] !== '\n';
    const newValue = before + (needsSpace ? ' @' : '@') + after;
    const newCursorPos = cursorPos + (needsSpace ? 2 : 1);
    onQuestionChange(newValue);
    handleInputChange(newValue, newCursorPos);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSlashCommandSelect = (command: SlashCommand) => {
    const { newValue, newCursorPosition } = handleSelectCommand(command);
    onQuestionChange(newValue);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  const triggerSlashCommand = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart || question.length;
    const before = question.substring(0, cursorPos);
    const after = question.substring(cursorPos);
    const needsSpace = before.length > 0 && before[before.length - 1] !== ' ' && before[before.length - 1] !== '\n';
    const newValue = before + (needsSpace ? ' /' : '/') + after;
    const newCursorPos = cursorPos + (needsSpace ? 2 : 1);
    onQuestionChange(newValue);
    handleSlashInputChange(newValue, newCursorPos);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <Wrapper>
      <FloraGradientWash />
      <Content>
        <BrandSection>
          <BrandRow>
            <FloraLogoGlow>
              <FloraGlowIcon src={icons.iconFlora} alt="Flora" />
            </FloraLogoGlow>
            <Text size="large" weight="bold" color="var(--ink-darkest)" style={{ fontSize: '32px' }}>
              Flora
            </Text>
          </BrandRow>
          <GreetingText size="large" weight="bold" color="var(--ink-darkest)">
            {greeting}
          </GreetingText>
        </BrandSection>

        <InputSection ref={dropZoneRef} $isDropTarget={isDraggingOver} {...dropHandlers}>
          {selectedMentions.length > 0 && (
            <MentionPillsContainer>
              <Group gap={6}>
                {selectedMentions.map((mention) => (
                  <MentionPill
                    key={`${mention.type}-${mention.id}`}
                    mention={mention}
                    onRemove={() => handlePillRemove(mention.id, mention.name)}
                  />
                ))}
              </Group>
            </MentionPillsContainer>
          )}
          {attachments.items.length > 0 && (
            <AttachmentChipsContainer>
              {attachments.items.map((attachment) => (
                <AgentAttachmentChip
                  key={attachment.localId}
                  attachment={attachment}
                  onRemove={attachments.removeAttachment}
                />
              ))}
            </AttachmentChipsContainer>
          )}
          <InputContainer ref={inputWrapperRef}>
            <MentionPopover
              isOpen={isMentionOpen && !isSlashOpen}
              items={allItems}
              boards={boards}
              searchQuery={mentionSearchQuery}
              onSelect={handleMentionSelect}
              onClose={closeMentionPopover}
              anchorRef={inputWrapperRef}
              dataTheme={dataTheme}
            />
            <SlashCommandPopover
              isOpen={isSlashOpen && !isMentionOpen}
              groups={filteredGroups}
              flatCommands={flatFilteredCommands}
              searchQuery={slashSearchQuery}
              onSelect={handleSlashCommandSelect}
              onClose={closeSlashPopover}
              anchorRef={inputWrapperRef}
              dataTheme={dataTheme}
            />
            <Textarea
              ref={textareaRef}
              // Land the caret in the chat input on mount so users can type
              // immediately without a click. This is the primary action of the
              // Flora landing screen (mirrors the sign-in email field pattern).
              autoFocus
              placeholder={FLORA_INPUT_PLACEHOLDER}
              value={question}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              autosize
              minRows={1}
              maxRows={6}
              variant="unstyled"
              styles={{
                input: {
                  fontSize: '16px',
                  lineHeight: '1.5',
                  padding: '0',
                  color: 'var(--ink-darkest)',
                  '&::placeholder': { color: 'var(--sky-dark)' },
                },
                wrapper: { flex: 1 },
              }}
              style={{ flex: 1 }}
            />
            <LandingToolbarRow>
              <LandingToolbarLeft>
                <UiTooltip label="Attach a file" position="top" withArrow variant="black">
                  <MentionTriggerBtn onClick={triggerFilePicker} type="button" aria-label="Attach file">
                    <IconifyIcon icon="material-symbols:add" width={18} height={18} />
                  </MentionTriggerBtn>
                </UiTooltip>
                <HiddenFileInput ref={fileInputRef} {...fileInputProps} />
                <UiTooltip label="Mention (@)" position="top" withArrow variant="black">
                  <MentionTriggerBtn onClick={triggerMention} type="button" aria-label="Mention">
                    <IconifyIcon icon="material-symbols:alternate-email" width={16} height={16} />
                  </MentionTriggerBtn>
                </UiTooltip>
                <UiTooltip label="Commands (/)" position="top" withArrow variant="black">
                  <MentionTriggerBtn onClick={triggerSlashCommand} type="button" aria-label="Slash command">
                    <IconifyIcon icon="material-symbols:terminal" width={16} height={16} />
                  </MentionTriggerBtn>
                </UiTooltip>
                <FloraApprovalModeToggle threadId={threadId} />
                <AgentModelSelector />
              </LandingToolbarLeft>
              <LandingToolbarRight>
                <SendButton onClick={handleSubmit} disabled={!canSubmit} aria-label="Send message" type="button">
                  <IconifyIcon icon="material-symbols:arrow-upward-rounded" width={18} height={18} />
                </SendButton>
              </LandingToolbarRight>
            </LandingToolbarRow>
          </InputContainer>
        </InputSection>

        <FloraPromptChips
          onSelect={(prompt) => {
            onQuestionChange(prompt);
            // Mirrors `canSubmit`: a suggested prompt must not start a turn
            // while a file is still uploading, or it would send without the
            // attachment the user is waiting on. The prompt still lands in
            // the composer, so they can send it once the upload settles.
            if (!isActiveQuestion && !attachments.hasUploading) {
              onSubmit(prompt);
            }
          }}
        />
      </Content>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: 40px 24px;
  box-sizing: border-box;
  position: relative;
`;

const Content = styled.div`
  max-width: 720px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  position: relative;
  z-index: 1;
`;

const BrandSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  margin-bottom: 20px;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const GreetingText = styled(Text)`
  text-align: center;
  font-size: 36px;
`;

const InputSection = styled.div<{ $isDropTarget: boolean }>`
  width: 100%;
  outline: ${({ $isDropTarget }) => ($isDropTarget ? '2px dashed var(--secondary-base)' : 'none')};
  outline-offset: 2px;
  border-radius: 12px;
`;

const MentionPillsContainer = styled.div`
  padding: 0 0 8px 0;
  display: flex;
  flex-wrap: wrap;
`;

const AttachmentChipsContainer = styled.div`
  padding: 0 0 8px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 12px 8px 16px;
  border: 1px solid var(--sky-light);
  border-radius: 16px;
  background: var(--base-white);
  box-shadow: 0 1px 4px var(--flora-shadow-input);
  transition: border-color 0.2s, box-shadow 0.2s;
  position: relative;

  &:focus-within {
    border-color: var(--flora-focus-border);
    box-shadow: 0 0 0 2px var(--flora-focus-ring);
  }
`;

const LandingToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LandingToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LandingToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MentionTriggerBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--ink-lighter);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: var(--ink-base);
    background: var(--flora-hover-subtle);
  }
`;

const SendButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: var(--flora-send-btn-bg);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    background: var(--sky-light);
    color: var(--sky-dark);
    cursor: not-allowed;
  }
`;
